import React from 'react';
import { Box } from '@mui/material';
import BoltIcon from '@mui/icons-material/Bolt';
import MapIcon from '@mui/icons-material/Map';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import ParameterSlider from '../../../common/inputs/ParameterSlider';
import { EEGTariff, Opex, Financing } from '../../../../types';
import { colors } from '../../../../styles/theme';

interface MainParametersSectionProps {
    eegTariff?: EEGTariff | null;
    opex?: Opex | null;
    financing?: Financing | null;
    canEdit: boolean;
    onZuschlagswertChange?: (value: string) => void;
    onPachtProzentChange?: (value: string) => void;
    onEkQuoteChange?: (value: string) => void;
    onLeaseTierPercentageChange?: (tierId: string, percentage: number) => void;
    projektLaufzeit: number;
}

/**
 * Haupt-Parameter Section
 * 
 * Zeigt die 3 wichtigsten Kalkulationsparameter als moderne, interaktive Slider:
 * - Zuschlagswert (EEG-Tarif)
 * - Pachtkosten in Anteil vom Erlös (OPEX)
 * - EK-Quote (Financing)
 * 
 * Diese Komponente wird außerhalb der Section-Komponente gerendert,
 * ähnlich wie die KPISection.
 */
const MainParametersSection: React.FC<MainParametersSectionProps> = ({
    eegTariff,
    opex,
    financing,
    canEdit,
    onZuschlagswertChange,
    onPachtProzentChange,
    onEkQuoteChange,
    onLeaseTierPercentageChange,
    projektLaufzeit,
}) => {
    // Pacht-Item aus OPEX holen (is_editable=false in pachtkosten section)
    const pachtItem = opex?.items?.find(item => item.section === 'pachtkosten' && !item.is_editable);
    const leaseTiers = pachtItem?.lease_tiers ?? [];

    return (
        <Box sx={{ mb: 8 }}>
            <Box
                sx={{
                    display: 'grid',
                    gridTemplateColumns: { xs: '1fr', md: 'repeat(3, 1fr)' },
                    gap: 3,
                }}
            >
                {/* Zuschlagswert (aus EEG-Tarif) */}
                <ParameterSlider
                    label="Zuschlagswert"
                    value={eegTariff?.zuschlagswert ?? 0}
                    min={0}
                    max={15}
                    step={0.1}
                    unit="Cent/kWh"
                    icon={<BoltIcon />}
                    color={colors.brand.primary}
                    onChange={(value) => onZuschlagswertChange?.(String(value))}
                    disabled={!canEdit || !onZuschlagswertChange}
                />

                {/* Pachtkosten in Anteil vom Erlös (aus OPEX) - mit Staffel-Hover */}
                <ParameterSlider
                    label="Pachtkosten"
                    min={0}
                    max={25}
                    step={0.5}
                    unit="%"
                    icon={<MapIcon />}
                    color={colors.brand.primary}
                    disabled={!canEdit}
                    leaseTiers={leaseTiers}
                    projektLaufzeit={projektLaufzeit}
                    onTierPercentageChange={(tierId, percentage) => {
                        onLeaseTierPercentageChange?.(tierId, percentage);
                    }}
                />

                {/* EK-Quote (aus Financing) */}
                <ParameterSlider
                    label="EK-Quote"
                    value={financing?.eigenkapitalquote ?? 0}
                    min={0}
                    max={100}
                    step={1}
                    unit="%"
                    icon={<AccountBalanceIcon />}
                    color={colors.brand.primary}
                    onChange={(value) => onEkQuoteChange?.(String(value))}
                    disabled={!canEdit || !onEkQuoteChange}
                />
            </Box>
        </Box>
    );
};

export default React.memo(MainParametersSection);
